/**
 * The player's own caves (docs/spec/07 §4): saved by the Construction Kit, listed under MY CAVES in
 * Practice. Stored as JSON cave definitions in localStorage.
 */

import type { CaveDef } from '../caves/types';
import type { KeyValueStore } from './storage';
import { KEYS } from './storage';

export interface SavedCave {
  /** Stable id for this saved cave (not the cave's letter). */
  readonly key: string;
  readonly def: CaveDef;
  /** ms since the epoch. */
  readonly saved: number;
}

const MAX_CAVES = 60;

function isDef(v: unknown): v is CaveDef {
  if (typeof v !== 'object' || v === null) return false;
  const d = v as Record<string, unknown>;
  return typeof d['name'] === 'string' && Array.isArray(d['map']) && Array.isArray(d['levels']) && Array.isArray(d['colors']);
}

export class CaveLibrary {
  constructor(private readonly store: KeyValueStore) {}

  list(): SavedCave[] {
    try {
      const raw = JSON.parse(this.store.load(KEYS.caves) ?? '[]') as unknown;
      if (!Array.isArray(raw)) return [];
      return raw
        .filter((e): e is SavedCave => typeof e === 'object' && e !== null && typeof (e as SavedCave).key === 'string' && isDef((e as SavedCave).def))
        .sort((a, b) => b.saved - a.saved);
    } catch {
      return [];
    }
  }

  get(key: string): SavedCave | undefined {
    return this.list().find((c) => c.key === key);
  }

  /** Save (or overwrite, when `key` is given) a cave; returns its key. False if storage is full. */
  save(def: CaveDef, now: number, key?: string): string {
    const k = key ?? `c${now.toString(36)}${Math.floor(now % 997).toString(36)}`;
    const rest = this.list().filter((c) => c.key !== k);
    const next = [{ key: k, def, saved: now }, ...rest].slice(0, MAX_CAVES);
    this.store.save(KEYS.caves, JSON.stringify(next));
    return k;
  }

  remove(key: string): void {
    this.store.save(KEYS.caves, JSON.stringify(this.list().filter((c) => c.key !== key)));
  }
}
